<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Colleges</h1>
        <button class="btn btn-primary" @click="openCreateModal">
          <b-icon icon="plus-circle" class="mr-1"></b-icon> Add College
        </button>
      </div>

      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <div class="row mb-3">
            <div class="col-md-4">
              <b-form-input
                v-model="filter"
                placeholder="Search colleges..."
                debounce="300"
              ></b-form-input>
            </div>
          </div>

          <b-table
            :items="colleges"
            :fields="fields"
            :filter="filter"
            :busy="loading"
            :per-page="perPage"
            :current-page="currentPage"
            responsive
            striped
            hover
            show-empty
          >
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading colleges...</div>
              </div>
            </template>
            <template #empty>
              <div class="text-center text-muted py-4">No colleges found.</div>
            </template>
            <template #cell(index)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>
            <template #cell(departments_count)="data">
              <b-badge variant="info">{{ data.value }}</b-badge>
            </template>
            <template #cell(actions)="data">
              <button class="btn btn-sm btn-info mr-1" @click="openEditModal(data.item)" :disabled="deleting">
                <b-icon icon="pencil"></b-icon>
              </button>
              <button class="btn btn-sm btn-danger" @click="confirmDelete(data.item)" :disabled="deleting">
                <b-spinner small v-if="deletingId === data.item.id"></b-spinner>
                <b-icon icon="trash" v-else></b-icon>
              </button>
            </template>
          </b-table>

          <div class="d-flex justify-content-between align-items-center" v-if="colleges.length > perPage">
            <small class="text-muted">Showing {{ colleges.length }} colleges</small>
            <b-pagination
              v-model="currentPage"
              :total-rows="colleges.length"
              :per-page="perPage"
              size="sm"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- Create/Edit Modal -->
      <b-modal
        v-model="showModal"
        :title="isEditing ? 'Edit College' : 'Add College'"
        @ok.prevent="saveCollege"
        :ok-disabled="saving"
        no-close-on-backdrop
      >
        <template #modal-ok>
          <b-spinner small v-if="saving" class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save' }}
        </template>
        <b-form @submit.prevent="saveCollege">
          <b-form-group label="College Name" label-for="college-name">
            <b-form-input
              id="college-name"
              v-model="form.college"
              placeholder="Enter college name"
              required
              :disabled="saving"
              :state="formErrors.college ? false : null"
            ></b-form-input>
            <b-form-invalid-feedback>{{ formErrors.college }}</b-form-invalid-feedback>
          </b-form-group>
        </b-form>
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      colleges: [],
      loading: false,
      saving: false,
      deleting: false,
      deletingId: null,
      filter: "",
      perPage: 20,
      currentPage: 1,
      showModal: false,
      isEditing: false,
      editId: null,
      form: { college: "" },
      formErrors: {},
      fields: [
        { key: "index", label: "#", class: "text-center", thStyle: { width: "60px" } },
        { key: "college", label: "College Name", sortable: true },
        { key: "departments_count", label: "Departments", class: "text-center", sortable: true },
        { key: "actions", label: "Actions", class: "text-center", thStyle: { width: "120px" } },
      ],
    };
  },
  created() {
    this.loadColleges();
  },
  methods: {
    loadColleges() {
      this.loading = true;
      axios
        .get("admin/colleges")
        .then((res) => {
          if (res.data.msg === "success") this.colleges = res.data.data;
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load colleges" }))
        .finally(() => (this.loading = false));
    },
    openCreateModal() {
      this.isEditing = false;
      this.editId = null;
      this.form = { college: "" };
      this.formErrors = {};
      this.showModal = true;
    },
    openEditModal(item) {
      this.isEditing = true;
      this.editId = item.id;
      this.form = { college: item.college };
      this.formErrors = {};
      this.showModal = true;
    },
    saveCollege() {
      this.formErrors = {};
      if (!this.form.college.trim()) {
        this.formErrors.college = "College name is required.";
        return;
      }
      this.saving = true;
      let request;
      if (this.isEditing) {
        request = axios.put("admin/colleges/" + this.editId, this.form);
      } else {
        request = axios.post("admin/colleges", this.form);
      }
      request
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: this.isEditing ? "College updated" : "College created" });
            this.showModal = false;
            this.loadColleges();
          } else {
            Toast.fire({ icon: "error", title: res.data.message || "Operation failed" });
          }
        })
        .catch((err) => {
          if (err.response && err.response.status === 422) {
            let data = err.response.data;
            if (data.errors) {
              this.formErrors = { college: Object.values(data.errors).flat().join(", ") };
            } else {
              Toast.fire({ icon: "error", title: data.message || "Validation error" });
            }
          } else {
            Toast.fire({ icon: "error", title: "An error occurred" });
          }
        })
        .finally(() => (this.saving = false));
    },
    confirmDelete(item) {
      Swal.fire({
        title: "Delete College?",
        text: `Are you sure you want to delete "${item.college}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleting = true;
          this.deletingId = item.id;
          axios
            .delete("admin/colleges/" + item.id)
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: "College deleted" });
                this.loadColleges();
              } else {
                Toast.fire({ icon: "error", title: res.data.message || "Delete failed" });
              }
            })
            .catch((err) => {
              let msg = err.response?.data?.message || "Failed to delete college";
              Toast.fire({ icon: "error", title: msg });
            })
            .finally(() => {
              this.deleting = false;
              this.deletingId = null;
            });
        }
      });
    },
  },
};
</script>
